import { MultiSelect } from "react-multi-select-component"
import ReactLoadingOverlay from "react-loading-overlay";
import ClipLoader from "react-spinners/ClipLoader";
import { memo, useState } from "react";
import { DashboardFilterProps, Option } from "./Dashboard.Type";
import refresh from "../../assets/images/refresh.svg";
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Select from "react-select";
import dayjs from "dayjs";
import DateRangePickerPopover, { DateRangeValue } from "./DateRangePickerPopover";

const customMessages = {
    allItemsAreSelected: "Select",
    selectSomeItems: "Select",
    selectAllFiltered: "Select",
};
const customDisplayValue = (selected: Option[]) => {
    if (selected.length === 0) {
        return "Select";
    }

    return `${selected.length} selected`;
};

const NOT_MAPPED_OPTION: Option = { label: "Not Mapped", value: "NOT_MAPPED" };

const idSelectStyles = {
    menu: (provided: any) => ({
        ...provided,
        width: "max-content",
        minWidth: "100%",
    }),
    option: (provided: any) => ({
        ...provided,
        whiteSpace: "nowrap",
    }),
};

const DashboardFilter = memo(({ request,
    onRequestChange,
    projectOptions,
    districtLoading,
    territoryLoading,
    stateOptions,
    territoryOptions,
    districtOptions,
    gtOptions,
    visitOptions,
    onDownload,
    onApply,
    onReset,
    isDataEmpty,
    photoDownloading }: DashboardFilterProps) => {
    const [selectedStates, setSelectedStates] = useState<Option[]>([]);
    const [selectedTerritories, setSelectedTerritories] = useState<Option[]>([]);
    const [selectedDistricts, setSelectedDistricts] = useState<Option[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<Option[]>([]);
    const [selectedGt, setSelectedGt] = useState<Option[]>([]);
    const [selectedVisit, setSelectedVisit] = useState<Option[]>([]);

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const handleDateClick = (event: any) => { setAnchorEl(event.currentTarget); };
    const handleDateClose = () => { setAnchorEl(null); };
    const open = Boolean(anchorEl);

    const formattedDateRange = (request.fromDate || request.toDate)
        ? `${request.fromDate ? dayjs(request.fromDate).format("DD/MM/YYYY") : '...'} - ${request.toDate ? dayjs(request.toDate).format("DD/MM/YYYY") : '...'}`
        : '';

    const stateDropdownOptions = [NOT_MAPPED_OPTION, ...stateOptions];
    const territoryDropdownOptions = [NOT_MAPPED_OPTION, ...territoryOptions];
    const districtDropdownOptions = [NOT_MAPPED_OPTION, ...districtOptions];
    const today = dayjs();

    const handleStateChange = (selected: Option[]) => {
        setSelectedStates(selected);
        setSelectedTerritories([]);
        setSelectedDistricts([]);
        setSelectedGt([]);
        setSelectedVisit([]);

        const isNotMappedSelected = selected.some(x => x.value === NOT_MAPPED_OPTION.value);
        const realSelections = selected.filter(x => x.value !== NOT_MAPPED_OPTION.value);

        onRequestChange({
            stateName: realSelections.map(x => x.label).join(","),
            isStateNotMapped: isNotMappedSelected,
            territoryName: "",
            isTerritoryNotMapped: false,
            districtName: "",
            isDistrictNotMapped: false,
            gtId: "",
            visitId: "",
        });
    };

    const handleTerritoryChange = (selected: Option[]) => {
        setSelectedTerritories(selected);
        setSelectedDistricts([]);
        setSelectedGt([]);
        setSelectedVisit([]);

        const isNotMappedSelected = selected.some(x => x.value === NOT_MAPPED_OPTION.value);
        const realSelections = selected.filter(x => x.value !== NOT_MAPPED_OPTION.value);

        onRequestChange({
            isTerritoryNotMapped: isNotMappedSelected,
            territoryName: realSelections.map(x => x.label).join(","),
            districtName: "",
            isDistrictNotMapped: false,
            gtId: "",
            visitId: "",
        });
    };

    const handleDistrictChange = (selected: Option[]) => {
        setSelectedDistricts(selected);
        setSelectedGt([]);
        setSelectedVisit([]);

        const isNotMappedSelected = selected.some(x => x.value === NOT_MAPPED_OPTION.value);
        const realSelections = selected.filter(x => x.value !== NOT_MAPPED_OPTION.value);

        onRequestChange({
            isDistrictNotMapped: isNotMappedSelected,
            districtName: realSelections.map(x => x.label).join(","),
            gtId: "",
            visitId: "",
        });
    };

    const handleGtChange = (option: any) => {
        setSelectedGt(option);
        setSelectedVisit([]);

        onRequestChange({
            gtId: option?.value ?? "",
            visitId: "",
        });
    };

    return (

        <div className="card min-w-full" style={{
            position: "relative",
            zIndex: 10,
        }} >
            <div className="card-body min-w-full pb-2">
                <div className="flex flex-wrap lg:flex-nowrap gap-2 items-end w-full">

                    {/* Project */}
                    <div className="flex-1 min-w-[80px]">
                        <label id="state">Project</label>
                        <MultiSelect
                            options={projectOptions}
                            value={selectedProjects}
                            onChange={(selected) => {
                                setSelectedProjects(selected);
                                onRequestChange({
                                    projectId: selected.map(x => x.label).join(",")
                                });
                            }}
                            hasSelectAll={false}
                            labelledBy="Project"
                            valueRenderer={customDisplayValue}
                            overrideStrings={customMessages}
                            shouldToggleOnHover={false}
                        />
                    </div>

                    {/* State */}
                    <div className="flex-1 min-w-[80px]">
                        <label id="state">State</label>
                        <MultiSelect
                            options={stateDropdownOptions}
                            value={selectedStates}
                            onChange={handleStateChange}
                            hasSelectAll={false}
                            labelledBy="state"
                            valueRenderer={customDisplayValue}
                            overrideStrings={customMessages}
                            shouldToggleOnHover={false}
                        />
                    </div>

                    {/* Territory */}
                    <div className="flex-1 min-w-[80px]">
                        <label id="territory">Territory</label>
                        {!territoryLoading ? (
                            <MultiSelect
                                options={territoryDropdownOptions}
                                value={selectedTerritories}
                                onChange={handleTerritoryChange}
                                disabled={territoryOptions.length === 0}
                                labelledBy="territory"
                                hasSelectAll={false}
                                valueRenderer={customDisplayValue}
                                overrideStrings={customMessages}
                                shouldToggleOnHover={false}
                            />
                        ) : (
                            <div style={{ position: "relative" }}>
                                <MultiSelect
                                    options={territoryDropdownOptions}
                                    value={selectedTerritories}
                                    onChange={handleTerritoryChange}
                                    labelledBy="territory"
                                    hasSelectAll={false}
                                    valueRenderer={customDisplayValue}
                                    overrideStrings={customMessages}
                                    disabled={true}
                                    disableSearch={true}
                                />
                                <ReactLoadingOverlay
                                    classNamePrefix="growindigo_"
                                    active={true}
                                    spinner={<ClipLoader color="#27878E" />}
                                    text={""}
                                >
                                </ReactLoadingOverlay>
                            </div>
                        )}
                    </div>

                    {/* District */}
                    <div className="flex-1 min-w-[80px]">
                        <label id="disctrict">District</label>
                        {!districtLoading ? (
                            <MultiSelect
                                options={districtDropdownOptions}
                                value={selectedDistricts}
                                onChange={handleDistrictChange}
                                disabled={districtOptions.length === 0}
                                labelledBy="disctrict"
                                hasSelectAll={false}
                                valueRenderer={customDisplayValue}
                                overrideStrings={customMessages}
                            />
                        ) : (
                            <div style={{ position: "relative" }}>
                                <MultiSelect
                                    options={districtDropdownOptions}
                                    value={selectedDistricts}
                                    onChange={handleDistrictChange}
                                    labelledBy="disctrict"
                                    hasSelectAll={false}
                                    valueRenderer={customDisplayValue}
                                    overrideStrings={customMessages}
                                    disabled={true}
                                    disableSearch={true}
                                />
                                <ReactLoadingOverlay
                                    classNamePrefix="growindigo_"
                                    active={true}
                                    spinner={<ClipLoader color="#27878E" />}
                                    text={""}
                                >
                                </ReactLoadingOverlay>
                            </div>
                        )}
                    </div>

                    {/* GtId */}
                    <div className="flex-1 min-w-[80px]">
                        <label>GT ID</label>
                        <Select
                            options={gtOptions}
                            isSearchable={true}
                            isClearable={true}
                            placeholder="Select"
                            value={selectedGt}
                            onChange={handleGtChange}
                            styles={idSelectStyles}
                        />
                    </div>

                    {/* Visit Id */}
                    <div className="flex-1 min-w-[80px]">
                        <label>Visit ID</label>
                        <Select
                            options={visitOptions}
                            isSearchable={true}
                            isClearable={true}
                            placeholder="Select"
                            value={selectedVisit}
                            onChange={(option: any) => {
                                setSelectedVisit(option);

                                onRequestChange({
                                    visitId: option?.value ?? "",
                                });
                            }}
                            styles={idSelectStyles}
                        />
                    </div>
                    {/* Date Range */}
                    <div className="flex-[2] min-w-[160px]">
                        <label>Date Range</label>
                        <FormControl fullWidth>
                            <TextField
                                size="small"
                                placeholder="Start Date - End Date"
                                value={formattedDateRange}
                                onClick={handleDateClick}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" edge="end" onClick={handleDateClick}>
                                                <CalendarTodayIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: "100%",
                                    "& .MuiInputBase-root": {
                                        height: 38,
                                        backgroundColor: "white",
                                        cursor: "pointer",
                                    },
                                    "& .MuiInputBase-input": {
                                        padding: "6px 8px",
                                        fontSize: "13px",
                                        cursor: "pointer",
                                    },
                                }}
                            />
                        </FormControl>

                        <DateRangePickerPopover
                            open={open}
                            anchorEl={anchorEl}
                            value={{ fromDate: request.fromDate ?? null, toDate: request.toDate ?? null }}
                            maxDate={today.toDate()}
                            onApply={(range: DateRangeValue) => {
                                onRequestChange({ fromDate: range.fromDate, toDate: range.toDate });
                                handleDateClose();
                            }}
                            onCancel={handleDateClose}
                        />
                    </div>
                    {/* Buttons */}
                    <div className="flex-none flex flex-wrap gap-2 items-center">
                        <button
                            className="bg-white text-[#406BBE] font-semibold border border-[#406BBE] px-4 py-[7px] rounded transition-all hover:bg-[#406BBE] hover:text-white active:bg-[#34508B] active:border-[#34508B] h-[38px] flex items-center justify-center"
                            onClick={() => {
                                onApply();
                            }}
                        >
                            Apply
                        </button>

                        <button onClick={() => {
                            setSelectedStates([]);
                            setSelectedTerritories([]);
                            setSelectedDistricts([]);
                            setSelectedProjects([]);
                            setSelectedGt([]);
                            setSelectedVisit([]);

                            onReset();
                        }}>
                            <img src={refresh} alt="home_icon" />
                        </button>

                        <button
                            className={`bg-white text-[#406BBE] font-semibold border border-[#406BBE] px-4 py-[7px] rounded transition-all whitespace-nowrap h-[38px] flex items-center justify-center ${isDataEmpty || photoDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#406BBE] hover:text-white active:bg-[#34508B] active:border-[#34508B]'}`}
                            onClick={() => {
                                if (!isDataEmpty && !photoDownloading && onDownload) {
                                    onDownload();
                                }
                            }}
                            disabled={isDataEmpty || photoDownloading}
                        >
                            {photoDownloading ? "Downloading..." : "GT Photo Download"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
});
export default DashboardFilter;